import { CircularProgress, Flex, Tag, TagLabel, Text } from '@chakra-ui/react'
import { useMemo } from 'react'
import { useICD10Groups } from '../../../../hooks/backend/icd10/useICD10Groups'

interface SuggestedICDCodesProps {
  codes: string[]
  onCodeClick: (code: string) => void
  loading: boolean
}

export const SuggestedICDCodes = ({ codes, onCodeClick, loading }: SuggestedICDCodesProps) => {
  const activeGroups = useICD10Groups()
  const codesWithDescriptions = useMemo(() => {
    const descMap = new Map(activeGroups.flatMap(g => g.codes.map(c => [c.code, c.description])))
    return codes.map(code => ({ code, description: descMap.get(code) ?? code }))
  }, [activeGroups, codes])

  let body: React.ReactNode = null
  if (loading) {
    body = (
      <Flex align="center" gap={2}>
        <CircularProgress color="green.500" isIndeterminate size={4} />
        <Text fontSize="xs" color="gray.500">
          Generating suggested ICD-10 codes...
        </Text>
      </Flex>
    )
  } else if (!codes.length) {
    body = (
      <Text fontSize="sm" color="gray.500" fontStyle="italic">
        No suggestions found — search or browse by category below.
      </Text>
    )
  } else {
    body = (
      <>
        {codesWithDescriptions.map(({ code, description }) => (
          <Tag
            key={code}
            size="sm"
            cursor="pointer"
            colorScheme="purple"
            onClick={() => onCodeClick(code)}
            _hover={{ opacity: 0.8 }}>
            <TagLabel>{description}</TagLabel>
          </Tag>
        ))}
      </>
    )
  }
  return (
    <Flex align="center" flexWrap="wrap" gap={3} pt={2} pb={1}>
      <Text fontSize="sm" color="gray.600" flexShrink={0} _dark={{ color: 'gray.400' }}>
        Suggested:
      </Text>
      {body}
    </Flex>
  )
}
